import { Plus, Trash2, TriangleAlert, Wallet } from '@/lib/icons'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { FieldBinding } from './NumberField'
import { NumberField } from './NumberField'
import { formatIntegerInput } from './numericInput'

/** Bosqich muharriridagi bitta qator — xom (matn) qiymatlar. */
export interface TierRow {
  views: string
  ratePer1000: string
}

export interface PayoutSectionProps {
  /** Bosqichli stavka jadvali (kamida 1 qator). Oxirgi qator "qolgan barchasi". */
  tiers: TierRow[]
  onTierChange: (index: number, field: keyof TierRow, value: string) => void
  onAddTier: () => void
  onRemoveTier: (index: number) => void
  tiersError: string | null
  /** Bosqichlar joriy saqlangan qiymatdan farq qilsa `true` — ogohlantirish shunga qarab. */
  tiersChanged: boolean
  autoPayoutThreshold: FieldBinding
  scheduledPayoutDay: FieldBinding
  disabled: boolean
}

/**
 * "💰 To'lov" bo'limi — `docs/03-app-flow.md` "Sahifa 6" maketi. Bosqichli (zinapoyali) stavka
 * jadvali + avtomatik to'lov chegarasi + rejali to'lov kuni. Har bosqich: prosmotr eni + shu
 * banddagi 1000 uchun stavka; oxirgi bosqich "qolgan barchasi" (eni yo'q). Hisob —
 * `calculateTieredAmount` (`@ayub/shared`).
 */
export function PayoutSection({
  tiers,
  onTierChange,
  onAddTier,
  onRemoveTier,
  tiersError,
  tiersChanged,
  autoPayoutThreshold,
  scheduledPayoutDay,
  disabled,
}: PayoutSectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wallet size={16} aria-hidden="true" /> To&apos;lov
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-app-text">Stavka bosqichlari</span>
            <span className="text-xs text-app-hint">1000 prosmotr uchun</span>
          </div>
          <p className="text-xs text-app-hint">
            Har bosqich prosmotr enigacha o&apos;z stavkasi bilan hisoblanadi. Oxirgi bosqich —
            qolgan barcha prosmotrlarga.
          </p>

          <div className="flex flex-col gap-2">
            {tiers.map((tier, index) => {
              const isLast = index === tiers.length - 1
              return (
                <div
                  key={index}
                  className="flex flex-wrap items-center gap-2 rounded-input border border-app-border bg-app-bg/50 px-2.5 py-2"
                >
                  <span className="w-16 shrink-0 text-xs font-medium text-app-hint">
                    {index + 1}-bosqich
                  </span>

                  <label className="flex flex-1 items-center gap-1.5">
                    <span className="shrink-0 text-xs text-app-hint">
                      {isLast ? 'Qolgan barchasi' : 'Birinchi'}
                    </span>
                    {!isLast && (
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        aria-label={`${index + 1}-bosqich prosmotr eni`}
                        value={tier.views}
                        onChange={(e) =>
                          onTierChange(index, 'views', formatIntegerInput(e.target.value))
                        }
                        disabled={disabled}
                        placeholder="30 000"
                        className="tabular h-10 w-24 rounded-input border border-app-border bg-app-bg px-2 text-right text-base text-app-text outline-none focus-visible:border-brand disabled:opacity-60"
                      />
                    )}
                    {!isLast && <span className="shrink-0 text-xs text-app-hint">prosmotr</span>}
                  </label>

                  <label className="flex items-center gap-1.5">
                    <span className="shrink-0 text-xs text-app-hint">stavka</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      aria-label={`${index + 1}-bosqich stavka`}
                      value={tier.ratePer1000}
                      onChange={(e) =>
                        onTierChange(index, 'ratePer1000', formatIntegerInput(e.target.value))
                      }
                      disabled={disabled}
                      placeholder="5 000"
                      className="tabular h-10 w-24 rounded-input border border-app-border bg-app-bg px-2 text-right text-base text-app-text outline-none focus-visible:border-brand disabled:opacity-60"
                    />
                    <span className="shrink-0 text-xs text-app-hint">so&apos;m</span>
                  </label>

                  <button
                    type="button"
                    onClick={() => onRemoveTier(index)}
                    disabled={disabled || tiers.length <= 1}
                    aria-label={`${index + 1}-bosqichni o'chirish`}
                    className="flex size-9 shrink-0 items-center justify-center rounded-button text-app-hint transition-colors hover:bg-app-card hover:text-stage-rejected-text disabled:opacity-40"
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>
              )
            })}
          </div>

          {tiersError && <p className="text-xs text-stage-rejected-text">{tiersError}</p>}

          {!disabled && (
            <button
              type="button"
              onClick={onAddTier}
              className="inline-flex w-fit items-center gap-1.5 rounded-button border border-app-border px-3 py-1.5 text-xs font-medium text-app-text transition-colors hover:bg-app-card"
            >
              <Plus size={14} aria-hidden="true" /> Bosqich qo&apos;shish
            </button>
          )}

          {/* Vazifa talabi: stavka o'zgarishi FAQAT yangi videolarga ta'sir qiladi — hisobi
              yopilgan videolar stavkani `POSTED`ga o'tganda muzlatib qo'ygan. */}
          {tiersChanged && (
            <p className="inline-flex items-start gap-1.5 rounded-button bg-urgency-today/10 px-2.5 py-2 text-xs font-medium text-urgency-today-text">
              <TriangleAlert size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
              Bu o&apos;zgarish FAQAT yangi videolarga ta&apos;sir qiladi. Hisobi allaqachon
              yopilgan videolar o&apos;z eski stavkasini saqlab qoladi.
            </p>
          )}
        </div>

        <div className="flex flex-col divide-y divide-app-border border-t border-app-border">
          <NumberField
            id="settings-auto-payout-threshold"
            label="Avtomatik to'lov chegarasi"
            unit="so'm"
            value={autoPayoutThreshold.value}
            onChange={autoPayoutThreshold.onChange}
            error={autoPayoutThreshold.error}
            disabled={disabled}
            placeholder="Masalan: 200000"
          />
          <NumberField
            id="settings-scheduled-payout-day"
            label="Rejali to'lov kuni"
            unit="-sana"
            value={scheduledPayoutDay.value}
            onChange={scheduledPayoutDay.onChange}
            error={scheduledPayoutDay.error}
            disabled={disabled}
            placeholder="Masalan: 5"
          />
        </div>
      </CardContent>
    </Card>
  )
}
